import {
  type Document,
  DocumentStatuses,
  DocumentType,
  signedBrokerChangesStatuses,
  signedEndorsementStatuses,
  signedTerminationStatuses,
  toSignStatuses
} from '@/types/documents'
import { isDocumentAvailable } from '@/utils/utils'
import type { ComposerTranslation } from 'vue-i18n'

export const isLifeProcedureRelatedDocument = (document: Document, lifeProceduredocuments: Document[]) => {
  return document.type === DocumentType.Contract && lifeProceduredocuments.length > 0
}

export const isPendingEnvelope = (document: Document) =>
  document.type === DocumentType.SignatureEnvelope && document.status === DocumentStatuses.Sent

export const isPendingSignature = (document: Document, getContractLifeProcedureDocuments: (id: string) => Document[]) =>
  isPendingEnvelope(document) ||
  (toSignStatuses.includes(document.status) && document.type !== DocumentType.LifeProcedure) ||
  isLifeProcedureRelatedDocument(document, getContractLifeProcedureDocuments(document.id))

export const isPendingNonSignatureDocument = (document: Document) =>
  !isPendingEnvelope(document) &&
  document.status !== DocumentStatuses.Processed &&
  document.status !== DocumentStatuses.Completed &&
  document.status !== DocumentStatuses.RequestSubmitted &&
  document.status !== DocumentStatuses.Archived &&
  document.status !== DocumentStatuses.Terminated &&
  !toSignStatuses.includes(document.status)

const isSignedTermination = (document: Document) =>
  document.type === DocumentType.Termination && signedTerminationStatuses.includes(document.status as DocumentStatuses)

const isSignedAmendment = (document: Document) =>
  document.type === DocumentType.Amendment && signedEndorsementStatuses.includes(document.status as DocumentStatuses)

const isSignedBrokerChange = (document: Document) =>
  (document.type === DocumentType.BrokerChange || document.type === DocumentType.Transfer) &&
  (signedBrokerChangesStatuses.includes(document.status as DocumentStatuses) ||
    document.status === DocumentStatuses.Archived ||
    document.status === DocumentStatuses.Terminated)

const isSignedDocument = (document: Document) =>
  isSignedTermination(document) || isSignedAmendment(document) || isSignedBrokerChange(document)

const isUnProcessedDocument = (document: Document) =>
  isPendingEnvelope(document) ||
  (document.status !== DocumentStatuses.Processed &&
    document.status !== DocumentStatuses.Completed &&
    document.status !== DocumentStatuses.RequestSubmitted &&
    !isSignedDocument(document))

const hasUnprocessedLifeProcedure = (
  document: Document,
  getContractLifeProcedureDocuments: (id: string) => Document[]
) =>
  isLifeProcedureRelatedDocument(document, getContractLifeProcedureDocuments(document.id)) &&
  getContractLifeProcedureDocuments(document.id)[0]?.status !== DocumentStatuses.Processed

export const hasPendingDocuments = (
  documents: Document[],
  getContractLifeProcedureDocuments: (id: string) => Document[]
) =>
  documents.some(
    (document) =>
      isUnProcessedDocument(document) || hasUnprocessedLifeProcedure(document, getContractLifeProcedureDocuments)
  )

export const ContractOrAmendmentDocument = (document: Document) => {
  return (
    document.type === DocumentType.Amendment ||
    document.type === DocumentType.Contract ||
    document.type === DocumentType.LifeProcedure
  )
}

export const isContractOrAmendmentDocument = (document: Document) =>
  [DocumentType.Amendment, DocumentType.Contract, DocumentType.LifeProcedure].includes(document.type)

export const hasPendingContractOrAmendment = (documents: Document[]) => {
  return documents.some((document) => isContractOrAmendmentDocument(document) && !isDocumentAvailable(document))
}

export const andMoreContractsLabel = (t: ComposerTranslation, otherContractsCount: number) =>
  otherContractsCount
    ? ` ${t(
        'pages.my_signatures.documents_to_sign.transfer.and_more',
        { count: otherContractsCount },
        { plural: otherContractsCount }
      )}`
    : ''
